import { Listbox, Transition } from "@headlessui/react";
import Image from "next/image";
import { FC, memo, Fragment, ReactNode, useState, useEffect } from "react";

import CheckIcon from "/public/icon/select-check.svg";
import { InputText } from "./InputText";
import { Checkbox } from "./Checkbox";

export type OptionProp = Array<{
  text: string;
  value: string | number | boolean;
  disabled?: boolean;
}>;

interface Props {
  name?: string;
  defaultValue?: string | number;
  placeholder?: string;
  options: OptionProp;
  className?: string;
  optionClassName?: string;
  value?: string | number | (string | number)[];
  onChange?: (value: string) => void;
  onChangeMultiple?: (value: string | number | (string | number)[]) => void;
  isDisabled?: boolean;
  isOptional?: boolean; // 未選択項目を作成します
  optionalLabel?: string;
  hasError?: boolean;
  onFocus?: (event: React.FocusEvent<HTMLButtonElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLButtonElement>) => void;
  onOptionsFocus?: () => void;
  onOptionsBlur?: () => void;
  isBreakAll?: boolean;
  textRight?: boolean;
  size?: string;
  hasSearchInput?: boolean;
  autoScroll?: boolean;
  multiple?: boolean;
  showCheckIcon?: boolean;
  defaultLabel?: string;
}

const SelectBase: FC<Props> = ({
  name,
  defaultLabel = '',
  placeholder,
  options,
  className,
  optionClassName,
  defaultValue,
  isDisabled = false,
  value,
  onChange,
  onFocus,
  onBlur,
  isOptional,
  optionalLabel,
  hasError,
  onOptionsFocus,
  onOptionsBlur,
  isBreakAll,
  textRight,
  size,
  hasSearchInput,
  autoScroll,
  multiple = false,
  showCheckIcon = true,
  onChangeMultiple
}) => {
  const classNames = ["relative", className].join(" ");
  const borderClass = hasError
    ? "border-accent focus:border-accent"
    : "border-color-gray-200";
  const textAlignClass = textRight ? "text-right pr-10" : "text-left pr-8";
  const textAlignClassOptions = textRight ? "justify-end pr-6" : "";
  const sizeClass = size === "sm" ? "h-9  min-w-[67px]" : "h-11";

  const selectedOptionText = (): string | ReactNode => {
    if (multiple) {
      const result = Array.isArray(value)
        ? options.filter((option) => value.includes(option.value as (string | number)))
        : [];
      if (result.length) {
        return result.map((item) => item.text).join(', ');
      }
      return placeholder ? (
        <span
          className={`block truncate ${
            defaultValue ? "text-theme" : "text-theme-light"
          }`}
        >
          {placeholder}
        </span>
      ) : defaultLabel ? defaultLabel :(
        ""
      );
    }
    const selected = options.find((option) => option.value === value);
    if (selected == null) {
      return placeholder ? (
        <span
          className={`block truncate ${
            defaultValue ? "text-theme" : "text-theme-light"
          }`}
        >
          {placeholder}
        </span>
      ) : defaultLabel ? defaultLabel :(
        ""
      );
    }
    return selected.text;
  };
  const [inputText, setInputText] = useState("");
  const [searchItems, setSearchItems] = useState<OptionProp>([]);

  useEffect(() => {
    setSearchItems(options);
  }, [options]);

  const handleFilterOptions = (search: string) => {
    const text = inputText?.trim();
    if (!text) {
      setSearchItems(options);
      return;
    }
    const results = options?.filter((option) => option.text.includes(text), []);
    setSearchItems(results);
  };

  useEffect(() => {
    const delayInputTimeoutId = setTimeout(() => {
      handleFilterOptions(inputText);
    }, 100);

    return () => clearTimeout(delayInputTimeoutId);
  }, [inputText]);

  const handleChange = (e: string) => {
    if (multiple) {
      const selectedItems = Array.isArray(value) ? [...value] : [];
      const exists = selectedItems.includes(e);
      const result = exists
        ? selectedItems.filter((v) => v !== e)
        : [...selectedItems, e]; 
      onChangeMultiple && onChangeMultiple(result);
      return;
    }
    if (onChange === undefined) {
      return;
    }
    onChange(e);
  };

  return (
    <Listbox
      name={name}
      value=""
      disabled={isDisabled}
      onChange={(e: string) => {
        handleChange(e);
      }}
    >
      <div className={classNames}>
        <Listbox.Button
          onFocus={onFocus}
          onBlur={onBlur}
          className={`relative py-2 pl-2 w-full placeholder:text-theme-light bg-white
          rounded border focus:border-primary focus:outline-0 text-sm 
          disabled:bg-[#F7F7F7] disabled:text-theme-medium
          ${borderClass} ${textAlignClass} ${sizeClass}`}
          onClick={() => {
            setInputText("");
            if (autoScroll) {
              setTimeout(() => {
                const itemHeight = 38; // 時刻設定用に決め内した値（(スクロール範囲の高さ/要素数)で算出）
                const scrollContainer = document.getElementById("auto-scroll") as HTMLElement;
                if (scrollContainer) {
                  scrollContainer.scrollTop = options.findIndex(x => x.value === value) * itemHeight;
                }
              }, 100); 
            }
          }}
        > 
          {value != null || (value && Array.isArray(value) && !(value as (string | number | boolean)[]).length) ? (
            <span className="block truncate">
              {selectedOptionText()}
            </span>
          ) : (
            <span
              className={`block truncate ${
                defaultValue ? "text-theme" : "text-theme-light"
              }`}
            >
              {defaultValue || placeholder || "選択されていません"}
            </span>
          )}
          <span className="flex absolute inset-y-0 right-0 items-center pr-2 pointer-events-none">
            <Image
              src="/user/icon/triangle-pulldown.svg"
              width={24}
              height={24}
              alt=""
            />
          </span>
        </Listbox.Button>
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Listbox.Options
            onFocus={onOptionsFocus}
            onBlur={onOptionsBlur}
            className="overflow-auto absolute z-10 py-1 mt-1 w-full max-h-60 text-base bg-white rounded-md ring-1 ring-black/5 shadow-lg sm:text-sm flex flex-col"
          >
            {hasSearchInput && (
              <div className="px-2 mb-2">
                <InputText
                  placeholder="絞り込み"
                  value={inputText}
                  onChange={(e) => {
                    setInputText(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                    }
                  }}
                  className="w-full h-[44px]"
                />
              </div>
            )}

            <div id="auto-scroll" className="overflow-auto z-10 py-1 w-full text-base bg-white focus:outline-none flex-1">
              {isOptional && (
                <Listbox.Option
                  value={undefined}
                  as={Fragment}
                  disabled={isDisabled}
                >
                  <li
                    className={`flex items-center py-2 px-4 hover:bg-theme-hover cursor-pointer select-none
                  ${isBreakAll ? "break-all" : ""}
                  ${textAlignClassOptions} ${optionClassName || ""}
                  `}
                  >
                    <CheckIcon className={`w-4 h-4 mr-2 invisible`} />
                    {optionalLabel ?? placeholder ?? "未選択"}
                  </li>
                </Listbox.Option>
              )}
              {
                multiple ? 
                  <>
                    {searchItems.length > 0 ? (
                      searchItems.map((item, idx) => (
                        <Listbox.Option
                          key={idx}
                          value={item.value}
                          as={"div"}
                          disabled={isDisabled || item.disabled}
                          onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                            handleChange(item.value as string); 
                            e.preventDefault();
                          }}
                        >
                          <li
                            className={`flex items-center py-2 px-4 hover:bg-theme-hover cursor-pointer select-none 
                        ${isBreakAll ? "break-all" : ""} 
                        ${Array.isArray(value) && value.includes(item.value as (string | number)) ? "bg-primary-thin" : "bg-white"} 
                        ${item.disabled ? "!bg-[#e9e9e9] !opacity-50" : ""}
                        ${textAlignClassOptions} ${optionClassName || ""}
                        `}
                          >
                            <Checkbox
                              checked={Array.isArray(value) && value.includes(item.value as (string | number))}
                              onChange={(e) => { e.preventDefault();}}
                              label={item.text}
                              labelClassName="!mb-0 !ml-2 font-normal"
                            />
                          </li>
                        </Listbox.Option>
                      ))
                    ) : (
                      <div className="text-center py-4">
                        <span className="text-[#BCBCBC]">
                          指定の条件は見つかりませんでした
                        </span>
                      </div>
                    )}
                  </>
                : 
                <>
                  {searchItems.length > 0 ? (
                    searchItems.map((item, idx) => (
                      <Listbox.Option
                        key={idx}
                        value={item.value}
                        as={Fragment}
                        disabled={isDisabled || item.disabled}
                      >
                        <li
                          className={`flex items-center py-2 px-4 hover:bg-theme-hover cursor-pointer select-none 
                      ${isBreakAll ? "break-all" : ""} 
                      ${value === item.value ? "bg-primary-thin" : "bg-white"} 
                      ${item.disabled ? "!bg-[#e9e9e9] !opacity-50" : ""}
                      ${textAlignClassOptions} ${optionClassName || ""}
                      `}
                        >
                          {showCheckIcon && <CheckIcon
                            className={`w-4 h-4 mr-2 ${
                              value === item.value ? "visible" : "invisible"
                            }`}
                          />}
                          {isBreakAll ? (
                            <span className={`${showCheckIcon ? 'w-[calc(100%-24px)' : 'w-full'}`}>{item.text}</span>
                          ) : (
                            <span className={`${showCheckIcon ? 'w-[calc(100%-24px)' : 'w-full'}`}>{item.text}</span>
                          )}
                        </li>
                      </Listbox.Option>
                    ))
                  ) : (
                    <div className="text-center py-4">
                      <span className="text-[#BCBCBC]">
                        指定の条件は見つかりませんでした
                      </span>
                    </div>
                  )}
                </>
              }
            </div>
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
};

export const Select = memo(SelectBase);
